import { useState } from 'react'
import { X } from 'lucide-react'
import { ProductSearchModal } from '../ProductSearchModal'
import { VariantSelectButton } from '../VariantSelectButton'
import type {
  ProductVariantSelection,
  SelectedVariant,
} from '../ProductSearchModal/types'

/**
 * Props for SingleVariantPicker component
 */
export interface SingleVariantPickerProps {
  // The product ID to restrict variant selection to
  productId: number | null

  // Currently selected variant (if any)
  selectedVariant: SelectedVariant | null

  // Callback when variant is selected or cleared
  onVariantSelect: (variant: SelectedVariant | null) => void

  // Configuration
  multiSelect?: boolean // Default: false (single variant selection)
  buttonText?: string // Default: "Select Variant"
  disabled?: boolean

  // Optional custom rendering for the selected variant display
  renderSelectedVariant?: (variant: SelectedVariant) => React.ReactNode
}

/**
 * Reusable SingleVariantPicker Component
 *
 * Displays a button to open variant selection modal or shows the selected variant
 * with an option to clear it. Handles the variant selection modal internally.
 * Designed for single variant selection use cases (e.g., Deals).
 *
 * @example
 * // Single variant selection
 * <SingleVariantPicker
 *   productId={product.id}
 *   selectedVariant={selectedVariant}
 *   onVariantSelect={handleVariantSelect}
 *   buttonText="Select Variant"
 * />
 */
export function SingleVariantPicker({
  productId,
  selectedVariant,
  onVariantSelect,
  multiSelect = false,
  buttonText = 'Select Variant',
  disabled = false,
  renderSelectedVariant,
}: SingleVariantPickerProps) {
  const [showModal, setShowModal] = useState(false)

  // Handle variant selection from modal
  const handleVariantSelect = (selection: ProductVariantSelection) => {
    // If no variants selected, clear the selection
    if (!selection.variants || selection.variants.length === 0) {
      onVariantSelect(null)
      setShowModal(false)
      return
    }

    // For single select, take the first variant
    const variant = selection.variants[0]
    if (variant) {
      onVariantSelect(variant)
    }

    setShowModal(false)
  }

  // Handle opening the modal
  const handleOpenModal = () => {
    if (!productId || disabled) return
    setShowModal(true)
  }

  // Handle clearing the selected variant
  const handleClearVariant = () => {
    onVariantSelect(null)
  }

  // Build initial selection for editing
  const initialSelection: ProductVariantSelection | undefined = selectedVariant
    ? {
        mode: 'variants-only' as const,
        variants: [selectedVariant],
      }
    : undefined

  return (
    <>
      {selectedVariant ? (
        // Show selected variant with clear button
        <div className="flex items-center justify-between gap-4 px-3 py-2 rounded-md border-[1px] border-brand-hover bg-surface-hover">
          <div className="flex-1">
            {renderSelectedVariant ? (
              renderSelectedVariant(selectedVariant)
            ) : (
              <>
                <div className="font-semibold text-brand">
                  {selectedVariant.name}
                </div>
                <div className="text-sm text-brand-hover mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                  {selectedVariant.format_pack_size && (
                    <span className="truncate font-bold">
                      {selectedVariant.format_pack_size}
                    </span>
                  )}
                  {selectedVariant.format_pack_size && selectedVariant.sku && (
                    <span className="truncate">|</span>
                  )}
                  {selectedVariant.sku && (
                    <span className="truncate">
                      <strong>SKU:</strong> {selectedVariant.sku}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={handleClearVariant}
            disabled={disabled}
            className="shrink-0 p-1 text-brand-hover hover:text-brand-pressed transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        // Show "Select Variant" button
        <VariantSelectButton
          onClick={handleOpenModal}
          text={buttonText}
          disabled={disabled || !productId}
        />
      )}

      {/* Variant Search Modal */}
      <ProductSearchModal
        show={showModal}
        onHide={() => setShowModal(false)}
        selectionMode="variants-only"
        onSelect={handleVariantSelect}
        title={selectedVariant ? 'Edit Variant' : buttonText}
        showProductFilter={false}
        multiSelect={multiSelect}
        maxSelections={multiSelect ? 100 : 1}
        restrictToProduct={productId || undefined}
        initialSelection={initialSelection}
      />
    </>
  )
}
